<template>
  <side-modal
    :show="show"
    @update:show="$emit('update:show', $event)"
    close-on-overlay-click
    location="right"
    width="500px"
  >
    <div>
      <div class="modal-top py-1">
        <span class="modal-title">Exclude Courses</span>
        <v-btn @click="$emit('update:show', false)" icon size="large">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>
      <div class="modal-body">
        <div class="d-flex justify-space-between w-100">
          <ButtonGroup
            v-if="props.academicTerm && props.advisoryTerm"
            :items="[
              { title: 'Academic', value: 'academic_sections' },
              { title: 'Advisory', value: 'advisory_sections' }
            ]"
            v-model="state.tab"
          ></ButtonGroup>
          <template v-else>
            <div style="font-size: 1.25rem; font-weight: 600" v-if="props.academicTerm">
              Academic
            </div>
            <div style="font-size: 1.25rem; font-weight: 600" v-if="props.advisoryTerm">
              Advisory
            </div>
          </template>
          <v-btn
            v-if="sections.academic_sections.length + sections.advisory_sections.length"
            @click="syncSections"
            :loading="state.loadingSections"
            >Refresh</v-btn
          >
        </div>

        <div class="table-overflow-hide">
          <div class="directoryTable scrollable-55vh mt-" style="height: auto; max-height: 55vh">
            <table class="table">
              <thead class="sticky-header">
                <tr>
                  <th style="width: 0px">
                    <v-checkbox
                      class="custom-input"
                      style="
                        max-width: max-content;
                        max-height: 30px;
                        overflow: hidden;
                        display: flex;
                        align-items: center;
                      "
                      density="compact"
                      :model-value="!!tableData.length && tableData.every((i) => i.excluded)"
                      @update:model-value="
                        {
                          if ($event) {
                            tableData.forEach((i) => (i.excluded = true));
                          } else {
                            tableData.forEach((i) => (i.excluded = false));
                          }
                        }
                      "
                    ></v-checkbox>
                  </th>

                  <th>
                    <div class="d-flex align-center justify-space-between gap-2">
                      <span> Section Name </span>
                      <v-text-field
                        class="custom-input"
                        label="Search"
                        color="primary"
                        density="compact"
                        bg-color="white"
                        style="max-width: 210px; text-transform: none"
                        v-model="state.search"
                        clearable
                      ></v-text-field>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in tableData" :key="row.section_id">
                  <td>
                    <v-checkbox
                      class="custom-input"
                      style="
                        max-width: max-content;
                        max-height: 24px;
                        overflow: hidden;
                        display: flex;
                        align-items: center;
                      "
                      density="compact"
                      v-model="row.excluded"
                    ></v-checkbox>
                  </td>
                  <td>{{ row.section_name }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div v-if="!tableData.length && loading" class="w-100">
          <LoadingState></LoadingState>
        </div>
        <div v-if="!tableData.length && !loading" class="w-100">
          <div class="empty-state">
            <figure style="max-width: 300px">
              <IconListEmptyState />
            </figure>
            <div class="title">We couldn't find any Courses.</div>
            <div class="w-100 d-flex justify-center">
              <v-btn @click="syncSections" :loading="state.loadingSections">Load</v-btn>
            </div>
          </div>
        </div>
        <div class="mt-4 d-flex justify-end">
          <v-btn v-if="tableData.length" :loading="state.loadSave" @click="save">Save</v-btn>
        </div>
      </div>
    </div>
  </side-modal>
</template>

<script setup lang="ts">
import type { BlackBaudIntegration } from "@/api_types/school";
import ButtonGroup from "@/components/ButtonGroup.vue";
import LoadingState from "@/components/loadingState.vue";
import SideModal from "@/components/sideModal.vue";
import FetchService from "@/services/fetch.service";
import { useAlertsStore } from "@/stores/alerts";
import { complexSearch } from "@/utils";
import { computed } from "vue";
import { reactive } from "vue";
import IconListEmptyState from "@/assets/icons/list-empty-state.svg";

type Sections = "academic_sections" | "advisory_sections";

const props = defineProps<{
  data: BlackBaudIntegration;
  show: boolean;
  academicTerm?: number;
  advisoryTerm?: number;
  building: number | null;
  sections: Record<Sections, { section_id: number; section_name: string; excluded: boolean }[]>;
  loading: boolean;
}>();
const emit = defineEmits<{
  (event: "update:show", val: boolean): void;
  (event: "reload"): void;
}>();

const state = reactive({
  tab: "academic_sections" as Sections,
  loadSave: false,
  search: "",
  loadingSections: false
});

const tableData = computed(() => {
  let tab = state.tab;
  if (props.advisoryTerm && !props.academicTerm) {
    tab = "advisory_sections";
  } else if (props.academicTerm && !props.advisoryTerm) {
    tab = "academic_sections";
  }
  return props.show
    ? props.sections[tab].filter((i) =>
        complexSearch(state.search || "", `${i.section_name} ${i.section_id}`)
      )
    : [];
});

const save = async () => {
  state.loadSave = true;
  const academic_term_id = props.academicTerm || undefined;
  const advisory_term_id = props.advisoryTerm || undefined;
  const res = await FetchService.fetchData("/integration/blackbaud/sections/exclude", "post", {
    academic_term_id,
    academic_section_ids: academic_term_id
      ? props.sections.academic_sections.filter((i) => i.excluded).map((i) => i.section_id)
      : undefined,
    advisory_term_id,
    advisory_section_ids: advisory_term_id
      ? props.sections.advisory_sections.filter((i) => i.excluded).map((i) => i.section_id)
      : undefined
  });
  if (!res?.academic_sections) {
    useAlertsStore().addAlert("error", "Failed to save excluded courses");
  }
  state.loadSave = false;
};

const syncSections = async () => {
  state.loadingSections = true;
  await FetchService.fetchData("*/blackbaud/sections?school_level=" + props.building, "get");
  emit("reload");
  state.loadingSections = false;
};
</script>

<style scoped lang="scss">
@use "@/assets/_vars.scss" as *;
@use "@/assets/_breakpoints.scss" as breakpoints;

.modal-top {
  display: flex;
  align-items: center;
  background-color: $primary;
  min-width: 100%;
  justify-content: space-between;
  .modal-title {
    font-weight: 600;
    font-size: 1.15rem;
    margin: auto 0 auto 20px;
    color: white;
  }
}
</style>
